import CropFreeIcon from '@mui/icons-material/CropFree';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CernQrCodeValue } from '../../api/types';
import { AdamsError, getErrorMessage } from '../../utils/errors';
import { cameraScanningErrorsNotToBeShown } from '../../utils/utils';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { QRScanner } from '../QRScanner/QRScanner';

interface ValidateQRScanProps {
    translations: Record<string, string>;
}

export const ValidateQRScan: React.FC<ValidateQRScanProps> = ({ translations }) => {
    const [nonceValue, setNonceValue] = useState<string | null>(null);
    const [accessPointCodeValue, setAccessPointCodeValue] = useState<string | null>(null);
    const [visitorNonceValue, setVisitorNonceValue] = useState<string | null>(null);
    const [scanError, setScanError] = useState<AdamsError | null>(null);
    const [displayQRScanner, setDisplayQRScanner] = useState(true);

    const navigate = useNavigate();

    const handleScanDetect = (value: CernQrCodeValue) => {
        setDisplayQRScanner(false);

        if (value) {
            const { nonce, accessPointCode, visitorNonce } = value;

            setScanError(null);
            if (nonce) {
                setNonceValue(nonce);
            } else if (accessPointCode) {
                setAccessPointCodeValue(accessPointCode);
            } else if (visitorNonce) {
                setVisitorNonceValue(visitorNonce);
            } else {
                setScanError({ message: translations.ERROR_NOT_CERN_QR_CODE });
            }
        } else {
            setScanError({ message: translations.ERROR_NOT_CERN_QR_CODE });
        }
    };

    const handleScanError = (error: AdamsError) => {
        const errorMessage = getErrorMessage(error);
        if (errorMessage && !cameraScanningErrorsNotToBeShown.includes(errorMessage)) {
            setDisplayQRScanner(false);
            setScanError(error);
        }
    };

    useEffect(() => {
        if (nonceValue) {
            navigate(`/functions/temporary-badge/${nonceValue}`);
        }

        if (accessPointCodeValue) {
            navigate(`/accesspoint/${accessPointCodeValue}`);
        }

        if (visitorNonceValue) {
            navigate(`/functions/visitor-card/${visitorNonceValue}`);
        }
    }, [accessPointCodeValue, nonceValue, visitorNonceValue, navigate]);

    const handleClose = () => {
        setDisplayQRScanner(false);
        navigate('/');
    };

    return (
        <Card>
            <CardContent>
                {scanError && (
                    <ErrorMessage
                        errorMessage={getErrorMessage(scanError)}
                        title={translations.ERROR_NOT_CERN_QR_CODE_TITLE}
                        translations={translations}
                    />
                )}

                {displayQRScanner && (
                    <QRScanner
                        onDetect={handleScanDetect}
                        onError={handleScanError}
                        onClose={handleClose}
                        translations={translations}
                    />
                )}

                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<CropFreeIcon style={{ fontSize: '1rem' }} />}
                    style={{ display: 'none' }}
                    onClick={() => setDisplayQRScanner(true)}
                >
                    {translations.GLOBAL_SCAN_BUTTON}
                </Button>
            </CardContent>
        </Card>
    );
};
